import {
    ISymbolSizeConfig,
    ISymbolStateConfig,
    SymbolStateTransition
} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {GGReelSymbol} from "./GGReelSymbol";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite} from "gsap";
import {GGGameModuleConfig} from "../GGGameModuleConfig";
import {GGGameConfig} from "../GGGameConfig";

export class GGReelSymbolH extends GGReelSymbol {
    private _bombSmokeAnimation:TimelineSprite;
    private _stopShake:boolean = true;
    private _shakeTween:TweenLite | null;

    constructor(symName:string, reelId:number, state?:ISymbolStateConfig, size?:ISymbolSizeConfig) {
        super(symName, reelId, state, size);
    }

    //for symbols: S,B
    protected createSymAnimation():void {
        if(this._symbolAnimations) {
            this._symbolAnimations.destroy();
        }
        this._symbolAnimations = this.createSpineAnimation(this.symName);
        this.addChild(this._symbolAnimations);
        this._symbolAnimations.visible = false;
        this._symbolAnimations.renderable = false;
    }

    public playWinAnimation():TimelineLite {
        return new TimelineLite();
    }

    public playMultiAnimation():TimelineLite {
        this.createSymAnimation();
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => {
            this.addChild(this._symbolAnimations);
            this._symbolAnimations.renderable = true;
            this._symbolAnimations.visible = true;
            this._symbolAnimations.state.setAnimation(0, "Multi", false);
        }, 0);
        tl.add(() => this.hideSymbolAnimation(), 0.83);

        return tl;
    }

    protected addNewStateAnimation(timeline:TimelineLite, newState:SymbolStateTransition, stateAnimation:TimelineSprite, delay:number):void {
        super.addNewStateAnimation(timeline, newState, stateAnimation, delay);
        this._stopShake = true
        if(newState.state.keyword === GGGameConfig.instance.SYMBOL_STATES.landing.keyword) {
            timeline.add(this.getLandingAnimation(), delay);
        }
        if(this.symName === "S") {
            if(newState.state.keyword === GGGameConfig.instance.SYMBOL_STATES.attention.keyword) {
                timeline.add(this.getPlusAnimation(), delay);
            } else if(newState.state.keyword === GGGameConfig.instance.SYMBOL_STATES.plus.keyword) {
                timeline.add(this.getIdleAnimation(), delay);
            }
        }
    }

    private getPlusAnimation():TimelineLite {
        this.createSymAnimation();
        return new TimelineLite().add([
            () => {
                this._symbolAnimations.renderable = true;
                this._symbolAnimations.visible = true;
                this._symbolAnimations.state.setAnimation(0, "Idle", false);
            }
        ]);
    }

    public playTriggerAnimation():TimelineLite {
        this.createSymAnimation();
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            () => {
                this._symbolAnimations.renderable = true;
                this._symbolAnimations.visible = true;
                this._symbolAnimations.state.setAnimation(0, "Win", false);
                for(let stateName in this._stateAnimations) {
                    const state = this._stateAnimations[stateName];
                    if(state != null) {
                        state.visible = false;
                    }
                }
            }
        ], 0);
        tl.add(() => {}, 1.36);
        return tl;
    }

    private shake(stateAnimation:TimelineSprite | null, frequency:number, maxOffset:number):void {
        this._stopShake = false;
        const offset:number = Math.floor(Math.random() * (maxOffset + maxOffset + 1) - maxOffset);
        this._shakeTween = TweenLite.fromTo(stateAnimation, frequency,
            {
                x : stateAnimation!.x + offset,
                y : stateAnimation!.y + offset
            },
            {
                x : stateAnimation!.x,
                y : stateAnimation!.y,
                onComplete : () => {
                    if(this._stopShake || (stateAnimation == null)) {
                        return;
                    }
                    this.shake(stateAnimation, 0.1, 2)
                },
                clearProps : "x,y"
            });
    }

    public getLandingAnimation():TimelineLite {
        const isBomb:boolean = this.symName[0] === "B";
        this.createSymAnimation();
        return new TimelineLite().add(() => {
            if(isBomb) {
                GGGameModuleConfig.soundPlayer.playBombLandingSound();
            }
            this._symbolAnimations.renderable = true;
            this._symbolAnimations.visible = true;
            this._symbolAnimations.state.setAnimation(0, "Landing", false);
        }).add(() => {
            if(isBomb) {
                this.shake(this._currentStateAnimation, 0.1, 2);
            } else {
                this.hideSymbolAnimation();
            }
        }, 0.8);
    }

    public getExplodeAnimation(stateAnimation:TimelineSprite):TimelineLite {
        if(this.symName === "S") {
            return new TimelineLite();
        }

        this.createSymAnimation();
        const tl:TimelineLite = new TimelineLite();
        if(!this._bombSmokeAnimation) {
            this._bombSmokeAnimation = new TimelineSprite(GameResources.getTextures("bombSmoke"));
            this._bombSmokeAnimation.name = "bombSmoke";
            this._bombSmokeAnimation.anchor.set(0.5, 0.5);
            this._bombSmokeAnimation.scale.set(1.8, 1.8);
            this._bombSmokeAnimation.position.set(0, 0);
            this._bombSmokeAnimation.hide();
        }

        tl.add([
            () => {
                this.addChild(this._bombSmokeAnimation);
                this._symbolAnimations.renderable = true;
                this._symbolAnimations.visible = true;
                this._symbolAnimations.state.setAnimation(0, "Explosion", false);
            },
            new TweenLite(stateAnimation, 0.3, {alpha : 0}),
            new TweenLite(this._symFrame, 0.3, {alpha : 0}),
            new TweenLite(this._symBackground, 0.3, {alpha : 0})
        ], 0);
        tl.add(this._bombSmokeAnimation.getAnimationAutoShowHide(true, true), 0.5);
        tl.add(() => this.hideSymbolAnimation(), 1.1);

        return tl;
    }

    public updateBackground(symHeight:number):void {
        super.updateBackground(symHeight);

        for(let stateName in this._stateAnimations) {
            const state = this._stateAnimations[stateName];
            if(state != null) {
                state.scale.set(this._displayScale, this._displayScale);
            }
        }
    }

    protected reset(remove:boolean) {
        super.reset(remove);
        this._stopShake = true;
        if(this._shakeTween) {
            this._shakeTween.pause();
            this._shakeTween.kill();
        }
        this._shakeTween = null;
    }
}